import { useEffect, useRef, useState, useCallback } from "react";
import { unitImages, images } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

// Cinematic reel — alternates Zirben + Landhaus authentic photography for an editorial promo film.
const buildScenes = (lang) => [
  { src: images.exterior,                       title: lang === "de" ? "Zirben Chalet"     : "Zirben Chalet",     sub: lang === "de" ? "Verschneite Stille · Pusterwald" : "Snowy silence · Pusterwald" },
  { src: images.zirbenInterior,                 title: lang === "de" ? "Warmes Zirbenholz" : "Warm stone pine",   sub: lang === "de" ? "Handgefertigt · Tradition"      : "Hand-crafted · tradition" },
  { src: unitImages.zirben.gallery[2],          title: lang === "de" ? "Wohnen am Kamin"   : "Living by the fire",sub: lang === "de" ? "Lange Abende · weite Räume"     : "Long evenings · open space" },
  { src: unitImages.zirben.gallery[3],          title: lang === "de" ? "Küche mit Stil"    : "Designer kitchen",  sub: lang === "de" ? "Voll ausgestattet · alpin"      : "Fully equipped · alpine" },
  { src: unitImages.landhaus.gallery[0],        title: lang === "de" ? "Landhaus Steiermark" : "Landhaus Steiermark", sub: lang === "de" ? "Bis zu 12 Gäste · 180 m²"      : "Up to 12 guests · 180 m²" },
  { src: unitImages.landhaus.gallery[2],        title: lang === "de" ? "Schlafzimmer im Landhaus" : "Bedrooms",   sub: lang === "de" ? "Massivholz · helle Räume"       : "Solid wood · bright rooms" },
  { src: unitImages.landhaus.gallery[6],        title: lang === "de" ? "Privater Naturgarten" : "Private garden", sub: lang === "de" ? "Teich · Hundeauslauf · Grill"  : "Pond · dog run · grill" },
  { src: unitImages.landhaus.gallery[1],        title: lang === "de" ? "Kaminzimmer"       : "Fireplace lounge",  sub: lang === "de" ? "Knisterndes Holz · 5 m Höhe"    : "Crackling wood · soaring ceiling" },
  { src: unitImages.zirben.gallery[5],          title: lang === "de" ? "Bergblick"         : "Mountain view",     sub: lang === "de" ? "Steirische Alpen · 360°"        : "Styrian Alps · 360°" },
  { src: images.exterior,                       title: lang === "de" ? "Dein Refugium wartet." : "Your retreat awaits.", sub: lang === "de" ? "chalet-steiermark.com" : "chalet-steiermark.com" },
];

const SCENE_MS = 4200;

export default function ChaletReel({ open, onClose, autoStart = true }) {
  const { lang } = useLang();
  const scenes = buildScenes(lang);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(autoStart);
  const timerRef = useRef(null);

  const advance = useCallback(() => {
    setIdx((i) => (i + 1) % scenes.length);
  }, [scenes.length]);

  useEffect(() => {
    if (!open) return;
    setIdx(0);
    setPlaying(autoStart);
  }, [open, autoStart]);

  useEffect(() => {
    if (!open || !playing) return;
    timerRef.current = setTimeout(advance, SCENE_MS);
    return () => clearTimeout(timerRef.current);
  }, [open, playing, idx, advance]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") { setIdx((i) => (i + 1) % scenes.length); }
      if (e.key === "ArrowLeft")  { setIdx((i) => (i - 1 + scenes.length) % scenes.length); }
      if (e.key === " ")           { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, scenes.length]);

  if (!open) return null;

  const progress = ((idx + 1) / scenes.length) * 100;

  return (
    <div
      data-testid="chalet-reel-modal"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col"
      role="dialog"
      aria-label="Chalet Steiermark Werbefilm"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 py-5 text-white">
        <div className="flex items-center gap-3">
          <span className="inline-block w-2 h-2 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
          <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[hsl(var(--accent))]">
            {lang === "de" ? "Werbefilm · Chalet Steiermark" : "Promo film · Chalet Steiermark"}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="reel-toggle"
            onClick={() => setPlaying((p) => !p)}
            className="text-[11px] tracking-[0.22em] uppercase border border-white/30 hover:border-white px-3 py-1.5 transition-colors"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? (lang === "de" ? "Pause" : "Pause") : (lang === "de" ? "Play" : "Play")}
          </button>
          <button
            data-testid="reel-close"
            onClick={onClose}
            className="text-[11px] tracking-[0.22em] uppercase border border-white/30 hover:border-white px-3 py-1.5 transition-colors"
            aria-label="Close"
          >
            ✕ {lang === "de" ? "Schließen" : "Close"}
          </button>
        </div>
      </div>

      {/* Stage */}
      <div className="relative flex-1 overflow-hidden">
        {scenes.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === idx ? "opacity-100 z-10" : "opacity-0 z-0"}`}
            aria-hidden={i !== idx}
          >
            <img
              src={s.src}
              alt={s.title}
              className={`w-full h-full object-cover ${i === idx ? "reel-kenburns" : ""}`}
              draggable={false}
            />
            {/* Cinematic letterbox + lower caption */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 18%, transparent 60%, rgba(0,0,0,0.78) 100%)",
            }} />
            <div className={`absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-20 pb-12 md:pb-16 text-white ${i === idx ? "reel-caption-in" : ""}`}>
              <div className="text-[11px] md:text-xs tracking-[0.26em] uppercase font-semibold text-[hsl(var(--accent))]">{s.sub}</div>
              <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-7xl leading-[0.95] mt-3" style={{ textShadow: "0 2px 28px rgba(0,0,0,0.7)" }}>
                {s.title}
              </h2>
            </div>
          </div>
        ))}

        {/* Side nav */}
        <button
          data-testid="reel-prev"
          onClick={() => { setIdx((i) => (i - 1 + scenes.length) % scenes.length); }}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 border border-white/40 hover:bg-white hover:text-black text-white transition-colors flex items-center justify-center"
          aria-label="Previous"
        >
          ←
        </button>
        <button
          data-testid="reel-next"
          onClick={advance}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 border border-white/40 hover:bg-white hover:text-black text-white transition-colors flex items-center justify-center"
          aria-label="Next"
        >
          →
        </button>
      </div>

      {/* Progress + counter */}
      <div className="px-6 md:px-10 py-5">
        <div className="flex items-center justify-between text-white/80 text-[11px] tracking-[0.22em] uppercase mb-3">
          <span>{String(idx + 1).padStart(2, "0")} / {String(scenes.length).padStart(2, "0")}</span>
          <span>{scenes[idx]?.title}</span>
        </div>
        <div className="h-px bg-white/15 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[hsl(var(--accent))] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
